import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, AlertTriangle, Pause } from "lucide-react";
import { useLanguage } from '@/contexts/LanguageContext';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  profileName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  mode?: 'delete' | 'suspend';
}

export const DeleteConfirmationModal = ({
  isOpen,
  profileName,
  isDeleting,
  onConfirm,
  onCancel,
  mode = 'delete',
}: DeleteConfirmationModalProps) => {
  const { t } = useLanguage();

  const isSuspend = mode === 'suspend';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${isSuspend ? 'bg-orange-100' : 'bg-red-100'}`}>
              <AlertTriangle className={`h-6 w-6 ${isSuspend ? 'text-orange-600' : 'text-red-600'}`} />
            </div>
            <DialogTitle className={`text-xl ${isSuspend ? 'text-orange-600' : 'text-red-600'}`}>
              {isSuspend ? t('profile.suspend.title') : t('profile.delete.title')}
            </DialogTitle>
          </div>
          <DialogDescription className="text-base leading-relaxed pt-2">
            {isSuspend ? t('profile.suspend.warning') : t('profile.delete.warning')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm font-medium text-foreground">
            {isSuspend
              ? t('profile.suspend.confirmQuestion', { name: profileName })
              : t('profile.delete.confirmQuestion', { name: profileName })
            }
          </p>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            disabled={isDeleting}
            className="border-gray-300 hover:border-foreground rounded-full"
          >
            {t('common.no')}
          </Button>
          <Button
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            disabled={isDeleting}
            className="gap-2 rounded-full"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {isSuspend ? t('profile.suspend.suspending') : t('profile.delete.deleting')}
              </>
            ) : (
              <>
                {isSuspend ? <Pause className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
                {t('common.yes')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
